import { ReactElement } from 'react'

import { FecImportMappingTab } from '../../../../enums/fecImportMappingTab'
import type { UseFetchFecAccountMappingsResult } from '../../../../hooks/useFetchFecAccountMappings'
import type { UseFetchFecJournalMappingsResult } from '../../../../hooks/useFetchFecJournalMappings'
import type { UseFetchFecThirdPartyMappingsResult } from '../../../../hooks/useFetchFecThirdPartyMappings'
import { FecImportChartOfAccountsTable } from '../FecImportChartOfAccountsTable'
import { FecImportJournalsTable } from '../FecImportJournalsTable'
import { FecImportMappingLoadError } from '../FecImportMappingLoadError'
import { FecImportThirdPartyTable } from '../FecImportThirdPartyTable'

interface FecImportMappingTabContentProps {
  accountMappingsResult: UseFetchFecAccountMappingsResult
  activeTab: FecImportMappingTab
  journalsResult: UseFetchFecJournalMappingsResult
  lockedJournalMappingIds: ReadonlySet<string>
  thirdPartyMappingsResult: UseFetchFecThirdPartyMappingsResult
}

export const FecImportMappingTabContent = ({
  accountMappingsResult,
  activeTab,
  journalsResult,
  lockedJournalMappingIds,
  thirdPartyMappingsResult,
}: FecImportMappingTabContentProps): ReactElement => {
  if (activeTab === FecImportMappingTab.Journals) {
    if (journalsResult.isError) {
      return (
        <FecImportMappingLoadError
          isRetrying={journalsResult.isRetrying}
          messageKey="settings.organization.accounting.fec_file_import.modal.mapping_step.journals.load_error"
          onRetry={journalsResult.retry}
        />
      )
    }

    return (
      <FecImportJournalsTable
        isLoading={journalsResult.isLoading}
        journalMappings={journalsResult.journalMappings}
        lockedMappingIds={lockedJournalMappingIds}
      />
    )
  }

  if (activeTab === FecImportMappingTab.ChartOfAccounts) {
    if (accountMappingsResult.isError) {
      return (
        <FecImportMappingLoadError
          isRetrying={accountMappingsResult.isRetrying}
          messageKey="settings.organization.accounting.fec_file_import.modal.mapping_step.chart_of_accounts.load_error"
          onRetry={accountMappingsResult.retry}
        />
      )
    }

    return (
      <FecImportChartOfAccountsTable
        accountMappings={accountMappingsResult.accountMappings}
        isLoading={accountMappingsResult.isLoading}
      />
    )
  }

  if (thirdPartyMappingsResult.isError) {
    return (
      <FecImportMappingLoadError
        isRetrying={thirdPartyMappingsResult.isRetrying}
        messageKey="settings.organization.accounting.fec_file_import.modal.mapping_step.third_party.load_error"
        onRetry={thirdPartyMappingsResult.retry}
      />
    )
  }

  return (
    <FecImportThirdPartyTable
      hasStatus={thirdPartyMappingsResult.hasExistingContacts}
      isLoading={thirdPartyMappingsResult.isLoading}
      thirdPartyMappings={thirdPartyMappingsResult.thirdPartyMappings}
    />
  )
}
